import React, { useState, useEffect, useCallback } from 'react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { 
  ExclamationCircleIcon, 
  CheckCircleIcon, 
  InformationCircleIcon, 
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowPathIcon,
  FunnelIcon,
  ClockIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';
import io from 'socket.io-client';

interface HardwareDevice {
  hardware_uuid: string;
  device_name: string;
  device_type: string;
  line_number: number;
  station_range: string;
  status: string;
}

interface HardwareLog {
  id: string;
  hardware_uuid: string;
  timestamp: string;
  log_level: 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  log_type: string;
  source: string;
  message: string;
  error_code?: number;
  operational_status?: string;
  station_number?: number;
  firmware_version?: string;
  device_name?: string;
  device_type?: string;
  acknowledged: boolean;
  acknowledged_by?: string;
  acknowledged_at?: string;
  notes?: string;
}

interface LogStats {
  hardware_uuid: string;
  device_name: string;
  debug_count: number;
  info_count: number;
  warning_count: number;
  error_count: number;
  critical_count: number;
  total_logs: number;
  last_log_time?: string;
}

const HardwareLogging: React.FC = () => {
  const { apiUrl } = useDatabaseContext();
  const [devices, setDevices] = useState<HardwareDevice[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [logs, setLogs] = useState<HardwareLog[]>([]);
  const [stats, setStats] = useState<LogStats[]>([]);
  const [criticalLogs, setCriticalLogs] = useState<HardwareLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [socket, setSocket] = useState<any>(null);
  const [filters, setFilters] = useState({
    logLevel: ['WARNING', 'ERROR', 'CRITICAL'] as string[], // Default to showing only important logs
    logType: '',
    unacknowledgedOnly: false,
    searchText: ''
  });
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Define fetchLogs first since it's used by other effects
  const fetchLogs = useCallback(async (hardwareUuid: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        hardware_uuid: hardwareUuid,
        limit: '100'
      });
      
      // Add filters to params properly
      if (filters.logLevel.length > 0) {
        params.set('log_level', filters.logLevel.join(','));
      }
      if (filters.logType) {
        params.set('log_type', filters.logType);
      }
      if (filters.unacknowledgedOnly) {
        params.set('unacknowledged_only', 'true');
      }
      
      const response = await fetch(`${apiUrl}/hardware-logs?${params}`);
      const data = await response.json();
      if (data.message === 'success') {
        setLogs(data.data.logs);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, apiUrl]); // Re-create when filters or apiUrl changes

  const fetchDevices = useCallback(async () => {
    try {
      const response = await fetch(`${apiUrl}/hardware-registry`);
      const data = await response.json();
      if (data.message === 'success') {
        setDevices(data.data.devices || []);
        // Select first device if none selected
        if (!selectedDevice && data.data.devices && data.data.devices.length > 0) {
          setSelectedDevice(data.data.devices[0].hardware_uuid);
        }
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    }
  }, [apiUrl, selectedDevice]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${apiUrl}/hardware-logs/stats`);
      const data = await response.json();
      if (data.message === 'success') {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [apiUrl]);

  const fetchCriticalLogs = useCallback(async () => {
    try {
      const response = await fetch(`${apiUrl}/hardware-logs/critical`);
      const data = await response.json();
      if (data.message === 'success') {
        setCriticalLogs(data.data);
      }
    } catch (error) {
      console.error('Error fetching critical logs:', error);
    }
  }, [apiUrl]);

  // Connect to WebSocket for real-time updates
  useEffect(() => {
    if (!apiUrl) return; // Guard against uninitialized apiUrl
    
    const socketUrl = apiUrl.replace('/api', '');
    const newSocket = io(socketUrl);
    
    newSocket.on('connect', () => {
      console.log('Connected to WebSocket for hardware logs');
    });
    
    setSocket(newSocket);
    
    return () => {
      newSocket.disconnect();
    };
  }, [apiUrl]);
  
  // Set up WebSocket listeners that depend on filters
  useEffect(() => {
    if (!socket) return;
    
    const handleNewLog = (data: any) => {
      if (data.action === 'new-log') {
        // Add new log to the list if it matches current filters
        if (!selectedDevice || data.log.hardware_uuid === selectedDevice) {
          // Only add the log if it matches the current filters
          const logLevel = data.log.log_level;
          if (filters.logLevel.length === 0 || filters.logLevel.includes(logLevel)) {
            if (!filters.unacknowledgedOnly || !data.log.acknowledged) {
              setLogs(prev => [data.log, ...prev].slice(0, 100)); // Keep last 100
            }
          }
        }
        // Update stats
        fetchStats();
      }
    };
    
    const handleAlert = (data: any) => {
      console.log('Hardware alert received:', data);
    };
    
    socket.on('hardware-log', handleNewLog);
    socket.on('hardware-alert', handleAlert);
    
    return () => {
      socket.off('hardware-log', handleNewLog);
      socket.off('hardware-alert', handleAlert);
    };
  }, [socket, filters, selectedDevice, fetchStats]);

  // Fetch hardware devices
  useEffect(() => {
    if (apiUrl) {
      fetchDevices();
      fetchStats();
      fetchCriticalLogs();
    }
  }, [apiUrl, fetchDevices, fetchStats, fetchCriticalLogs]); // Add all function dependencies
  
  // Fetch logs when selected device changes or filters change
  useEffect(() => {
    if (selectedDevice) {
      fetchLogs(selectedDevice);
    }
  }, [selectedDevice, fetchLogs]); // fetchLogs changes when filters change

  // Auto-refresh logs
  useEffect(() => {
    if (autoRefresh && selectedDevice) {
      const interval = setInterval(() => {
        fetchLogs(selectedDevice);
      }, 5000); // Refresh every 5 seconds
      
      return () => clearInterval(interval);
    }
  }, [autoRefresh, selectedDevice, fetchLogs]); // fetchLogs includes filters

  const acknowledgeLog = async (logId: string, notes?: string) => {
    try {
      const response = await fetch(`${apiUrl}/hardware-logs/${logId}/acknowledge`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes, acknowledged_by: 'current-user' })
      });
      
      if (response.ok) {
        // Refresh logs
        if (selectedDevice) {
          fetchLogs(selectedDevice);
        }
        fetchCriticalLogs();
      }
    } catch (error) {
      console.error('Error acknowledging log:', error);
    }
  };

  const clearLogs = async () => {
    if (!confirm('Are you sure you want to clear ALL hardware logs? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`${apiUrl}/hardware-logs/clear`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        console.log('All hardware logs cleared');
        setLogs([]);
        setCriticalLogs([]);
        fetchStats();
        if (selectedDevice) {
          fetchLogs(selectedDevice);
        }
      } else {
        console.error('Failed to clear logs');
      }
    } catch (error) {
      console.error('Error clearing logs:', error);
    }
  };

  const getLogIcon = (level: string) => {
    switch (level) {
      case 'DEBUG': return <CpuChipIcon className="h-4 w-4" />;
      case 'INFO': return <InformationCircleIcon className="h-4 w-4" />;
      case 'WARNING': return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'ERROR': return <ExclamationCircleIcon className="h-4 w-4" />;
      case 'CRITICAL': return <XCircleIcon className="h-4 w-4" />;
      default: return <InformationCircleIcon className="h-4 w-4" />;
    }
  };

  const getLogColor = (level: string) => {
    switch (level) {
      case 'DEBUG': return 'text-gray-500';
      case 'INFO': return 'text-blue-500';
      case 'WARNING': return 'text-yellow-500';
      case 'ERROR': return 'text-orange-500';
      case 'CRITICAL': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const deviceStats = selectedDevice ? stats.find(s => s.hardware_uuid === selectedDevice) : null;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Hardware Logging</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 ${
              autoRefresh 
                ? 'bg-indigo-600 text-white' 
                : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
            }`}
          >
            <ArrowPathIcon className={`h-4 w-4 ${autoRefresh ? 'animate-spin' : ''}`} />
            Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
          </button>
          <button
            onClick={clearLogs}
            className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
          >
            Clear All Logs
          </button>
        </div>
      </div>

      {/* Critical Alerts */}
      {criticalLogs.length > 0 && (
        <div className="bg-red-50 border border-red-500 rounded-lg p-4">
          <div className="flex items-center">
            <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
            <div className="flex-1">
              <div className="font-semibold mb-2">Critical Hardware Alerts ({criticalLogs.length})</div>
              <div className="space-y-1">
                {criticalLogs.slice(0, 3).map(log => (
                  <div key={log.id} className="flex justify-between items-center text-sm">
                    <span>{log.device_name}: {log.message}</span>
                    <button
                      onClick={() => acknowledgeLog(log.id)}
                      className="px-3 py-1 text-xs bg-white text-red-600 border border-red-300 rounded hover:bg-red-50"
                    >
                      Acknowledge
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Device Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {devices.map(device => {
            const deviceStat = stats.find(s => s.hardware_uuid === device.hardware_uuid);
            const hasErrors = (deviceStat?.error_count || 0) + (deviceStat?.critical_count || 0) > 0;
            
            return (
              <button
                key={device.hardware_uuid}
                onClick={() => {
                  setSelectedDevice(device.hardware_uuid);
                  // fetchLogs is now called by useEffect when selectedDevice changes
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${
                  selectedDevice === device.hardware_uuid
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{device.device_name}</span>
                {hasErrors && (
                  <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                    {(deviceStat?.error_count || 0) + (deviceStat?.critical_count || 0)}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {selectedDevice && (
        <div className="space-y-4">
          {/* Device Stats */}
          {deviceStats && (
            <div className="grid grid-cols-6 gap-4">
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-2xl font-bold text-gray-500">{deviceStats.debug_count || 0}</div>
                <div className="text-sm text-gray-500">Debug</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-2xl font-bold text-blue-500">{deviceStats.info_count || 0}</div>
                <div className="text-sm text-gray-500">Info</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-2xl font-bold text-yellow-500">{deviceStats.warning_count || 0}</div>
                <div className="text-sm text-gray-500">Warning</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-2xl font-bold text-orange-500">{deviceStats.error_count || 0}</div>
                <div className="text-sm text-gray-500">Error</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-2xl font-bold text-red-500">{deviceStats.critical_count || 0}</div>
                <div className="text-sm text-gray-500">Critical</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-2xl font-bold">{deviceStats.total_logs || 0}</div>
                <div className="text-sm text-gray-500">Total (24h)</div>
              </div>
            </div>
          )}


          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex gap-4 items-center flex-wrap">
              <div className="flex gap-2 items-center">
                <span className="text-sm font-medium">Level:</span>
                {['DEBUG', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'].map(level => (
                  <button
                    key={level}
                    onClick={() => {
                      setFilters(prev => ({
                        ...prev,
                        logLevel: prev.logLevel.includes(level)
                          ? prev.logLevel.filter(l => l !== level)
                          : [...prev.logLevel, level]
                      }));
                    }}
                    className={`px-2 py-1 text-xs rounded ${
                      filters.logLevel.includes(level)
                        ? `bg-indigo-100 text-indigo-800 ${getLogColor(level)}`
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filters.unacknowledgedOnly}
                  onChange={(e) => setFilters(prev => ({ ...prev, unacknowledgedOnly: e.target.checked }))}
                  className="rounded"
                />
                <label className="text-sm">Unacknowledged Only</label>
              </div>
              <button
                onClick={() => selectedDevice && fetchLogs(selectedDevice)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 flex items-center gap-2"
              >
                <FunnelIcon className="h-4 w-4" />
                Apply Filters
              </button>
            </div>
          </div>

          {/* Logs List */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Log Entries</h3>
            </div>
            <div className="p-4">
              {loading ? (
                <div className="text-center py-8">Loading logs...</div>
              ) : logs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No logs found</div>
              ) : (
                <div className="space-y-2">
                  {logs.map(log => (
                    <div
                      key={log.id}
                      className={`p-3 border rounded-lg ${log.acknowledged ? 'bg-gray-50' : 'bg-white'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={getLogColor(log.log_level)}>
                            {getLogIcon(log.log_level)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-1 rounded text-xs ${getLogColor(log.log_level)} bg-gray-100`}>
                                {log.log_level}
                              </span>
                              <span className="px-2 py-1 rounded text-xs bg-gray-100">
                                {log.log_type}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatTimestamp(log.timestamp)}
                              </span>
                              {log.source && (
                                <span className="text-xs text-gray-400">
                                  {log.source}
                                </span>
                              )}
                            </div>
                            <div className="text-sm">{log.message}</div>
                            {log.error_code && (
                              <div className="text-xs text-gray-500 mt-1">
                                Error Code: {log.error_code}
                              </div>
                            )}
                            {log.acknowledged && (
                              <div className="text-xs text-green-600 mt-1">
                                ✓ Acknowledged {log.acknowledged_at && `at ${formatTimestamp(log.acknowledged_at)}`}
                                {log.notes && ` - ${log.notes}`}
                              </div>
                            )}
                          </div>
                        </div>
                        {!log.acknowledged && (log.log_level === 'ERROR' || log.log_level === 'CRITICAL') && (
                          <button
                            onClick={() => acknowledgeLog(log.id)}
                            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                          >
                            Acknowledge
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HardwareLogging;